import React, { useState, useEffect } from 'react';
import { 
  Inbox, ChevronLeft, ChevronRight, User, CheckCircle, XCircle, Shield
} from 'react-feather';
import { API_URL, fetchWithAuth, getImageUrl } from '../../api';
import { useAuth } from '../../context/AuthContext';

const EventRequestTab = ({ eventId }) => {
  const { user } = useAuth();

  // --- ESTADOS ---
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Control de Permisos
  const [hasPermission, setHasPermission] = useState(false);
  
  // Control de UI
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animDirection, setAnimDirection] = useState('');
  const [acceptAllLoading, setAcceptAllLoading] = useState(false);

  // --- CARGAR DATOS ---
  useEffect(() => {
    const fetchPermsAndRequests = async () => {
      try {
        setLoading(true);

        const hierarchy = await fetchWithAuth(`${API_URL}/events/${eventId}/team-hierarchy`);
        const myNode = hierarchy.find(n => n.user_id === user?.id);

        const isMaxAuth =
          user?.role === 'admin' ||
          myNode?.role_label === 'CREADOR DEL EVENTO' ||
          myNode?.depth === 1;

        if (!isMaxAuth) {
          setHasPermission(false);
          setLoading(false);
          return;
        }

        setHasPermission(true);

        const data = await fetchWithAuth(`${API_URL}/events/${eventId}/qr-requests`);
        const rawPending = Array.isArray(data) ? data.filter(r => !r.approved) : [];

        // Enriquecer datos si falta el objeto user
        const enrichedRequests = await Promise.all(rawPending.map(async (req) => {
            if (req.user) return req;
            if (req.user_id) {
                try {
                    const userData = await fetchWithAuth(`${API_URL}/users/${req.user_id}`);
                    return { ...req, user: userData };
                } catch (e) {
                    return { 
                        ...req, 
                        user: { name: `Usuario #${req.user_id}`, username: 'Desconocido' } 
                    };
                }
            }
            return req;
        }));

        setRequests(enrichedRequests);
      } catch (err) {
        console.error("Error fetching requests:", err);
        setError('No se pudieron cargar las solicitudes.');
      } finally {
        setLoading(false);
      }
    };

    if (eventId && user) fetchPermsAndRequests();
  }, [eventId, user]);

  // --- ACTIONS ---
  const handleApprove = async (reqEventId, userId) => {
    try {
      await fetchWithAuth(`${API_URL}/approve-qr-request/${reqEventId}/${userId}`, { method: 'POST' });
      setRequests(prev => prev.filter(req => !(req.event_id === reqEventId && req.user_id === userId)));
    } catch (err) { alert("Error al aprobar"); }
  };

  const handleReject = async (reqEventId, userId) => {
    try {
      await fetchWithAuth(`${API_URL}/reject-qr-request/${reqEventId}/${userId}`, { method: 'POST' });
      setRequests(prev => prev.filter(req => !(req.event_id === reqEventId && req.user_id === userId)));
    } catch (err) { alert("Error al rechazar"); }
  };

  // --- NAVEGACIÓN ---
  const handleNext = () => {
    if (currentIndex < requests.length - 1) {
      setAnimDirection('slide-left');
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        setAnimDirection('');
      }, 300);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setAnimDirection('slide-right');
      setTimeout(() => {
        setCurrentIndex(prev => prev - 1);
        setAnimDirection('');
      }, 300);
    }
  };

  const handleActionWithAnimation = (type) => {
    if (requests.length === 0) return;
    const currentReq = requests[currentIndex];
    const uId = currentReq.user_id || currentReq.user?.id;
    const eId = currentReq.event_id;

    if (!uId || !eId) return;

    setAnimDirection(type === 'approve' ? 'slide-up' : 'slide-down');
    
    setTimeout(async () => {
      if (type === 'approve') await handleApprove(eId, uId);
      else await handleReject(eId, uId);

      if (currentIndex >= requests.length - 1) {
        setCurrentIndex(Math.max(0, requests.length - 2));
      }
      setAnimDirection('');
    }, 300);
  };

  const handleAcceptAll = async () => {
    if (!window.confirm(`¿Aprobar a las ${requests.length} personas pendientes?`)) return;
    setAcceptAllLoading(true);
    try {
      for (const req of requests) {
        const uId = req.user_id || req.user?.id;
        if (uId) await fetchWithAuth(`${API_URL}/approve-qr-request/${req.event_id}/${uId}`, { method: 'POST' });
      }
      setRequests([]);
      setCurrentIndex(0);
    } catch (err) { alert('Hubo un error al procesar.'); } 
    finally { setAcceptAllLoading(false); }
  };

  // --- RENDERIZADO ---
  if (loading) return <div className="py-20 text-center text-white/50 animate-pulse">Cargando...</div>;

  // 🔴 VISTA DE ACCESO DENEGADO 🔴
  if (!hasPermission) {
    return (
      <div className="w-full max-w-5xl mx-auto pb-20 pt-16 px-4 fade-in">
         <div className="bg-black/40 backdrop-blur-md rounded-2xl border border-red-500/20 min-h-[400px] flex flex-col items-center justify-center p-8 text-center shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-red-500/5 blur-[100px] pointer-events-none"></div>
            <Shield size={64} className="text-red-400 mb-6 opacity-80" />
            <h2 className="text-2xl font-bold text-white mb-2 tracking-wide">Acceso Restringido</h2>
            <p className="text-gray-400 max-w-md">
               Solo el <span className="text-white font-medium">Creador del Evento</span> y los <span className="text-white font-medium">Socios</span> tienen autorización para revisar y aceptar las solicitudes de acceso.
            </p>
         </div>
      </div>
    );
  }

  // 🟢 VISTA NORMAL DE SOLICITUDES (Si tiene permisos) 🟢
  return (
    <div className="w-full max-w-5xl mx-auto pb-20 pt-16 fade-in px-4">
       <div className="bg-black/40 backdrop-blur-md rounded-2xl border border-white/10 min-h-[600px] flex flex-col relative overflow-hidden shadow-2xl">
          
          {/* Header */}
          <div className="p-6 border-b border-white/10 bg-white/5 lg:flex md:flex items-center justify-between z-30 relative">
             <h2 className="text-xl font-light tracking-wider mb-[10px] text-white">SOLICITUDES PENDIENTES</h2>
             <span className={`px-3 py-1 text-xs rounded-full border ${requests.length > 0 ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/20' : 'bg-white/10 text-white/40 border-white/10'}`}>
                {requests.length} Pendientes
             </span>
          </div>

          {/* CUERPO PRINCIPAL */}
          <div className="flex-1 flex items-center justify-center p-4 relative w-full">
              {requests.length === 0 ? (
                  <div className="text-center animate-fade-in z-10">
                      <div className="p-6 bg-white/5 rounded-full mb-6 inline-block">
                          <Inbox size={48} className="text-white/40" />
                      </div>
                      <h3 className="text-xl font-medium text-white mb-2">Todo al día</h3>
                      <p className="text-gray-400 text-sm">No hay solicitudes pendientes.</p>
                  </div>
              ) : (
                  <>
                     {/* Navegación Izquierda */}
                     {currentIndex > 0 && (
                        <button onClick={handlePrev} className="absolute left-0 top-0 bottom-0 w-20 md:w-32 z-20 flex items-center justify-center group focus:outline-none">
                             <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"/>
                             <div className="relative p-4 rounded-full bg-black/50 border border-white/10 text-white shadow-xl backdrop-blur-sm group-hover:bg-white group-hover:text-black group-hover:scale-110 transition-all duration-300 transform translate-x-[-10px] group-hover:translate-x-2">
                                <ChevronLeft size={32} />
                             </div>
                        </button>
                     )}

                     {/* Navegación Derecha */}
                     {currentIndex < requests.length - 1 && (
                        <button onClick={handleNext} className="absolute right-0 top-0 bottom-0 w-20 md:w-32 z-20 flex items-center justify-center group focus:outline-none">
                             <div className="absolute inset-0 bg-gradient-to-l from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"/>
                             <div className="relative p-4 rounded-full bg-black/50 border border-white/10 text-white shadow-xl backdrop-blur-sm group-hover:bg-white group-hover:text-black group-hover:scale-110 transition-all duration-300 transform translate-x-[10px] group-hover:-translate-x-2">
                                <ChevronRight size={32} />
                             </div>
                        </button>
                     )}

                     {/* CARD CENTRAL */}
                     <div 
                        className={`w-full max-w-sm bg-[#111] rounded-3xl border border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden relative z-10 transition-all duration-500 ease-out transform
                        ${animDirection === 'slide-left' ? '-translate-x-[150%] opacity-0 rotate-[-5deg]' : ''}
                        ${animDirection === 'slide-right' ? 'translate-x-[150%] opacity-0 rotate-[5deg]' : ''}
                        ${animDirection === 'slide-up' ? '-translate-y-full opacity-0 scale-95' : ''}
                        ${animDirection === 'slide-down' ? 'translate-y-full opacity-0 scale-95' : ''}`}
                     >
                        {requests[currentIndex] && (
                            <div className="p-8 flex flex-col items-center text-center">
                                <div className="relative mb-6">
                                    <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full"></div>
                                    <div className="w-28 h-28 relative rounded-full border-4 border-[#222] shadow-2xl overflow-hidden bg-zinc-800 flex items-center justify-center">
                                        {requests[currentIndex].user?.profile_image_url ? (
                                            <img src={getImageUrl(requests[currentIndex].user.profile_image_url)} alt="User" className="w-full h-full object-cover"/>
                                        ) : (
                                            <span className="text-4xl font-bold text-white">
                                                {requests[currentIndex].user?.name?.charAt(0).toUpperCase() || '?'}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <h3 className="text-2xl font-bold text-white mb-1">{requests[currentIndex].user?.name || 'Usuario'}</h3>
                                <p className="text-purple-400 font-mono text-sm mb-6 bg-purple-500/10 px-3 py-1 rounded-full border border-purple-500/20">
                                    @{requests[currentIndex].user?.username || 'user'}
                                </p>

                                <div className="grid grid-cols-2 gap-4 w-full mb-4">
                                    <button onClick={() => handleActionWithAnimation('reject')} className="py-4 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 transition-all flex flex-col items-center justify-center gap-1 group">
                                        <XCircle size={24} className="mb-1 group-hover:scale-110 transition-transform"/>
                                        <span className="text-xs font-bold tracking-wider">RECHAZAR</span>
                                    </button>

                                    <button onClick={() => handleActionWithAnimation('approve')} className="py-4 rounded-xl bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/20 transition-all flex flex-col items-center justify-center gap-1 group">
                                        <CheckCircle size={24} className="mb-1 group-hover:scale-110 transition-transform"/>
                                        <span className="text-xs font-bold tracking-wider">APROBAR</span>
                                    </button>
                                </div>

                                <button onClick={handleAcceptAll} disabled={acceptAllLoading} className="w-full py-3 rounded-xl bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-colors text-xs font-medium tracking-widest uppercase border border-white/5">
                                    {acceptAllLoading ? 'Procesando...' : 'Aceptar Todas'}
                                </button>

                                <div className="mt-6 text-xs text-white/20">
                                    Solicitud {currentIndex + 1} de {requests.length}
                                </div>
                            </div>
                        )}
                     </div>
                  </>
              )}
          </div>
       </div>
       
       {error && <div className="mt-4 text-center text-red-400 text-sm">{error}</div>}
    </div>
  );
};

export default EventRequestTab;